import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { usePortal } from 'reactApp/hooks/usePortal';
import { PromoSelectors } from 'reactApp/modules/promo/promo.selectors';
import { EPromoType } from 'reactApp/modules/promo/promo.types';
import { getPublicOwner } from 'reactApp/modules/public/public.selectors';
import { useUploadInputHandlers } from 'reactApp/modules/uploading/hooks/useUploadInputHandlers';
import { Button } from 'reactApp/ui/Button/Button';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { UploadBlock } from 'reactApp/ui/UploadBlock/UploadBlock';
import { UploadInput } from 'reactApp/ui/UploadInput/UploadInput';

import styles from './PublicUploadHeader.css';

interface IProps {
    firstName: string;
    lastName: string;
    nick: string;
    avatar: string;
    email: string;
    ownerEmail: string;
    showBubble: boolean;
    onUploadClick();
    onBubbleShow();
}

export const PublicUploadHeader: React.FC<IProps> = ({ firstName, lastName, nick, avatar, ownerEmail, onUploadClick }) => {
    const publicUploadPromo = useSelector(PromoSelectors.getPromo(EPromoType.publicUpload));
    const uploadPortalTarget = usePortal('uploadDropArea');
    const publicOwner = Boolean(useSelector(getPublicOwner));
    const ref = useRef<HTMLDivElement>(null);

    const { subscribeToInput, unsubscribeOfInput, processDrop } = useUploadInputHandlers();

    useEffect(() => {
        if (publicUploadPromo) {
            publicUploadPromo.onShow();
        }
    }, [publicUploadPromo]);

    const name = `${firstName ? firstName : ''} ${lastName ? lastName : ''}`.trim() || nick || ownerEmail;

    return (
        <div className={styles.root}>
            <div className={styles.content}>
                <UploadInput />
                <div className={styles.avatar} style={{ backgroundImage: `url(${avatar})` }} />
                {!!name && (
                    <div>
                        <div className={styles.name}>{name}</div>
                        <div className={styles.text}>сделал эту папку общей и разрешил загружать в нее файлы</div>
                    </div>
                )}
                <div className={styles.button}>
                    <div ref={ref}>
                        <Button primary size="big" onClick={onUploadClick} theme="octavius">
                            Загрузить файлы
                        </Button>
                    </div>
                    {!!publicUploadPromo && (
                        <FloatingTooltip
                            target={ref}
                            placement={ETooltipPlacement.bottomStart}
                            size={ETooltipSize.medium}
                            title="Загружайте файлы вместе"
                            text="В папку может грузить файлы любой, у кого есть ссылка"
                            onClose={publicUploadPromo?.onClose}
                            qaId="tooltip-public-upload"
                        />
                    )}
                </div>
            </div>
            {createPortal(
                <UploadBlock
                    showDropArea={publicOwner}
                    allowDnd={true}
                    gaCategory="public"
                    subscribeToInput={subscribeToInput}
                    unsubscribeOfInput={unsubscribeOfInput}
                    processDrop={processDrop}
                />,
                uploadPortalTarget
            )}
        </div>
    );
};
